import { useEffect, useState } from 'react';
import { ytReady, ytOrigin } from './ytApi';

// Module-scoped so a real second visit renders instantly.
const cache = {};
// One promise per list id rather than a boolean: every mount waiting on the
// same read resolves from it. A boolean only ever notified the mount that
// started the probe, which React 18's double-mount then throws away.
const reads = {};
const titleCache = {};

// How many titles are asked for at once. A playlist can hold a few hundred
// reels, and asking for all of them at once queues that many requests at
// youtube.com and re-renders the grid on every one that lands.
const TITLE_BATCH = 10;

const fetchTitle = (id) => {
  if (titleCache[id]) return Promise.resolve([id, titleCache[id]]);
  return fetch(`https://www.youtube.com/oembed?format=json&url=https://www.youtube.com/watch?v=${id}`)
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => {
      if (!d || !d.title) return null;
      titleCache[id] = d.title;
      return [id, d.title];
    })
    // Private or removed video — the tile keeps its "Reel N" fallback.
    .catch(() => null);
};

/**
 * Reads titles a batch at a time, reporting each batch as one update.
 *
 * `alive` is asked between batches rather than once at the start, so a
 * client who leaves the section stops the remaining reads instead of
 * paying for a playlist they are no longer looking at.
 */
const fetchTitles = (ids, onTitles, alive) => {
  const run = (from) => {
    if (from >= ids.length || !alive()) return;
    const batch = ids.slice(from, from + TITLE_BATCH);
    Promise.all(batch.map(fetchTitle)).then((pairs) => {
      if (!alive()) return;
      const found = pairs.reduce((acc, pair) => {
        if (pair) acc[pair[0]] = pair[1];
        return acc;
      }, {});
      if (Object.keys(found).length) onTitles(found);
      run(from + TITLE_BATCH);
    });
  };
  run(0);
};

/**
 * Reads a playlist's video ids with a hidden 1x1 player: the queue is asked of
 * the player itself, so no API key is involved. Resolves with an empty array
 * when the API never arrives, and clears its entry so a later mount can retry
 * rather than waiting on a poisoned one.
 */
const readPlaylist = (listId) => {
  if (reads[listId]) return reads[listId];

  reads[listId] = new Promise((resolve) => {
    let settled = false;
    const finish = (queue) => {
      if (settled) return;
      settled = true;
      if (queue.length) cache[listId] = queue;
      // Only a successful read is worth keeping; a failure must be retryable.
      else delete reads[listId];
      resolve(queue);
    };

    // Covers the API script never arriving, so it is cleared the moment the
    // player exists: past that point the poll below owns the deadline. Long,
    // because a cold first load pays for the script download and the probe's
    // own iframe before onReady can fire.
    const giveUp = setTimeout(() => finish([]), 15000);

    ytReady(() => {
      const host = document.createElement('div');
      host.style.cssText =
        'position:absolute;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none';
      document.body.appendChild(host);

      const probe = new window.YT.Player(host, {
        width: 1,
        height: 1,
        host: 'https://www.youtube-nocookie.com',
        playerVars: { list: listId, listType: 'playlist', playsinline: 1, ...ytOrigin() },
        events: {
          onReady: () => {
            clearTimeout(giveUp);
            // The queue is not populated the instant onReady fires.
            let tries = 0;
            const timer = setInterval(() => {
              tries += 1;
              const queue = probe.getPlaylist ? probe.getPlaylist() || [] : [];
              if (!queue.length && tries <= 20) return;

              clearInterval(timer);
              try {
                probe.destroy();
              } catch (e) {
                /* already gone */
              }
              host.remove();
              finish(queue);
            }, 250);
          },
        },
      });
    });
  });

  return reads[listId];
};

export const usePlaylistVideos = (listId) => {
  const [ids, setIds] = useState(() => cache[listId] || []);
  const [titles, setTitles] = useState(titleCache);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!listId) return undefined;

    let alive = true;
    // One update per batch rather than per title: a hundred reels re-render
    // the grid ten times instead of a hundred.
    const onTitles = (found) =>
      alive && setTitles((prev) => ({ ...prev, ...found }));
    const isAlive = () => alive;

    if (cache[listId]) {
      setIds(cache[listId]);
      setFailed(false);
      fetchTitles(cache[listId], onTitles, isAlive);
      return () => {
        alive = false;
      };
    }

    setIds([]);
    setFailed(false);
    // Every mount awaits the same read, whether or not it started it. A failed
    // read is retried once: the first probe pays for a cold API, and the second
    // runs warm — the same reason leaving the tab and coming back used to work.
    readPlaylist(listId)
      .then((queue) => (queue.length || !alive ? queue : readPlaylist(listId)))
      .then((queue) => {
        if (!alive) return;
        if (!queue.length) {
          setFailed(true);
          return;
        }
        setIds(queue);
        fetchTitles(queue, onTitles, isAlive);
      });

    return () => {
      alive = false;
    };
  }, [listId]);

  return { ids, titles, failed };
};
